import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonGroup, Button } from 'react-bootstrap';
import { flatMap, pickBy } from 'lodash';
import PropTypes from 'prop-types';
import qs from 'qs';

import RequestSearchSelect from '../../../../../components/Select/RequestSearchSelect';
import Dropdown from '../../../../../components/Dropdown/Default';
import Select from '../../../../../components/Select/Custom';
import VirtualSelect from '../../../../../components/Select/Virtual';

import { setPageFiltersQuery, getUsersList } from '../../../../../redux/actions/reports';
import { reportsSelector } from '../../../../../redux/selectors/reports';

const Filters = ({ filters }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const reports = useSelector((state) => reportsSelector(state));
  const { users, pageFiltersQuery } = reports;

  const requestRef = useRef(null);

  const queryString = qs.parse(location.search.slice(1));

  const [localFilters, setLocalFilters] = useState({
    mode: filters?.modes?.find((mode) => mode.selected) || null,
    year: filters?.years?.find((year) => year.selected) || null,
    users: null,
    statuses: null,
    services: null,
  });

  const flatServices = (services) => flatMap(services?.list, (item) => item.services)
    ?.filter((item) => item.selected)
    ?.map((item) => item.id);

  const activeFilters = useMemo(
    () => ({
      users: filters?.users?.map((user) => ({ ...user, login: user.title })),
      services: flatServices(filters?.services) || [],
      statuses: filters?.statuses?.list?.filter((status) => status.selected),
    }),
    [filters],
  );

  const onChangeMode = (mode) => {
    requestRef.current?.clearState();
    setLocalFilters((prevState) => ({ ...prevState, mode }));
    dispatch(setPageFiltersQuery(pickBy({ ...pageFiltersQuery, mode: mode.code, services: null, statuses: null, users: null })));
    history.push({
      pathname: location.pathname,
      search: qs.stringify(pickBy({ ...queryString, mode: mode.code })),
    });
  };

  const onChangeYear = (year) => {
    requestRef.current?.clearState();
    setLocalFilters((prevState) => ({ ...prevState, year: filters?.years?.find((item) => item.id === year) || null }));
    dispatch(setPageFiltersQuery({ ...pageFiltersQuery, year: filters?.years?.find((item) => item.id === year)?.id?.toString() || null }));
    history.push({
      pathname: location.pathname,
      search: qs.stringify(pickBy({ ...queryString, year })),
    });
  };

  const onChangeUsers = (options) => {
    setLocalFilters((prevState) => ({ ...prevState, users: options?.map((user) => user.id) || null }));
  };

  const onChangeServices = (options) => {
    setLocalFilters((prevState) => ({ ...prevState, services: options || null }));
  };

  const onChangeStatuses = (options) => {
    const statuses = options?.length === filters?.statuses?.list?.length ? null : options?.map((status) => status.id) || null;
    setLocalFilters((prevState) => ({ ...prevState, statuses }));
  };

  const onSubmitFilters = () => {
    dispatch(
      setPageFiltersQuery({ ...pageFiltersQuery, users: localFilters?.users, statuses: localFilters?.statuses, services: localFilters?.services }),
    );
  };

  useEffect(() => {
    const statuses = filters?.statuses?.all_selected
      ? null
      : filters?.statuses?.list?.filter((status) => status.selected)?.map((status) => status.id) || null;
    const services = filters?.services?.all_selected ? null : flatServices(filters?.services);
    setLocalFilters({
      mode: filters?.modes?.find((mode) => mode.selected) || null,
      year: filters?.years?.find((year) => year.selected) || null,
      users: filters?.users?.map((user) => user.id) || null,
      statuses,
      services,
    });
  }, [filters]);

  return (
    <div className="reports-filters__items">
      {filters?.modes?.length ? (
        <div className="reports-filters__items-block">
          <ButtonGroup>
            {filters?.modes?.map((mode) => (
              <Button
                key={mode.code}
                active={mode.code === localFilters?.mode?.code}
                onClick={() => onChangeMode(mode)}
              >
                {mode?.title}
              </Button>
            ))}
          </ButtonGroup>
        </div>
      ) : null}

      {filters?.years?.length ? (
        <div className="reports-filters__items-block">
          <Dropdown
            handleSelect={(key) => onChangeYear(key)}
            title={localFilters?.year?.title || ''}
            items={filters?.years}
            classForBtnDrop="font__weight-med"
            dropStyle="default"
            pullRight={false}
            simple
          />
        </div>
      ) : null}

      <div className="reports-filters__items-block users">
        <RequestSearchSelect
          ref={requestRef}
          onRequestFunc={getUsersList}
          options={users}
          values={activeFilters.users}
          onChange={onChangeUsers}
          labelName="Users"
          name="users"
          title="Users"
          id="users-search"
          needUpdateValues
        />
      </div>

      {filters?.services?.list?.length ? (
        <div className="reports-filters__items-block services">
          <VirtualSelect
            toggleBtnTitle="Services"
            optionsTitle="services"
            onChange={(options) => onChangeServices(options)}
            options={filters?.services?.list}
            selectedOptions={activeFilters.services}
            menuWidth={340}
            showLoader={false}
            toggleComponent={({ values }) => (
              <>
                {values?.length === 0 ? (
                  'All services'
                ) : (
                  <>
                    Services
                    {' '}
                    <span className="options-count">{`(${values.length})`}</span>
                  </>
                )}
              </>
            )}
            group
            groupKey="services"
            showOptionId
            needUpdateSelected
          />
        </div>
      ) : null}

      {filters?.statuses?.list?.length ? (
        <div className="reports-filters__items-block methods">
          <Select
            id="orders-statuses"
            labelName="Status"
            title="Statuses"
            fieldName="status"
            options={filters?.statuses?.list}
            values={activeFilters.statuses}
            onSelect={onChangeStatuses}
          />
        </div>
      ) : null}

      <div className="reports-filters__items-block">
        <Button bsStyle="primary" onClick={() => onSubmitFilters()}>
          Submit
        </Button>
      </div>
    </div>
  );
};

Filters.propTypes = {
  filters: PropTypes.object,
};

export default Filters;
